import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NotePencil, Spinner } from "@phosphor-icons/react";
import PhoneInput, {
  Country,
  isPossiblePhoneNumber,
  isValidPhoneNumber,
  Value as PhoneInputValueType,
} from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useSearchParams } from "react-router-dom";
import { PopupWindow } from "@shopflo/ui/popup";
import OTPForm from "components/auth/forms/OTPForm";
import PrimaryButton from "components/common/buttons/PrimaryButton";
import CustomComponentSlot from "components/common/CustomComponentSlot";
import ConsentCheckboxes from "components/common/consent/ConsentCheckboxes";
import { ConsentForm } from "lib/hooks/useConsentForm";
import MarketingConsent from "components/common/MarketingConsent";
import { CaptchaWrapper, CaptchaState, CaptchaStatus } from "components/common/CaptchaWrapper";
import { useAuthContext } from "lib/contexts/AuthProvider";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { useUserConsentContext } from "lib/contexts/UserConsentProvider";
import { useSSOContext } from "lib/contexts/SSOProvider";
import { useUserContext } from "lib/contexts/UserProvider";
import { getRequest } from "lib/core/apiClient";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import useForm, { FormField } from "lib/hooks/useForm";
import { useJsonPermission } from "lib/hooks/useJsonPermission";
import useInterval from "lib/hooks/useInterval";
import { useLocale } from "lib/hooks/useLocale";
import { invokeTruecallerDeeplink } from "lib/third-party/truecaller";
import ToggleLoadingText from "components/common/ToggleLoadingText";
import { OTPChannel, OTPVerificationDetails, UserLoginType } from "lib/types/user";
import {
  OTPDeliveryType,
  resentOtp,
  sendOtpWithPhoneNumber,
  setAuthCookiesInLocalStorage,
  verifyOtp,
} from "lib/utils/auth";
import { analyticsEvents, analyticsTypes, constants, eventTypes } from "lib/utils/constants";
import {
  addDefaultCountryCode,
  classNames,
  getErrorCode,
  getPhoneInputMaxLength,
  inIframe,
  isMobile,
  isThirdPartyCookieEnabled,
  publishPostMessage,
  setPhoneNumberSpacing,
} from "lib/utils/helpers";
import { errorToast } from "lib/utils/toasters";
import { shopfloBroadcastChannel, CheckoutMessageTypes } from "lib/utils/broadcastChannel";
import { ThirdPartyData, useThirdPartyIntegration } from "lib/hooks/useThirdPartyIntegration";

interface LoginProps {
  onSuccess?: (...args: any) => void;
  setCurrentWindow: (value: PopupWindow) => void;
  setAuthToken: (value: string) => void;
  onOtpDialogChange?: (isOtpDialog: boolean) => void;
  validSSORequestId?: string | null;
  /** DPDPA consent state, owned by the popup so its views can replace the card */
  consentForm?: ConsentForm;
}

let pollCounter = 1;
let isPhoneInputFirstFocus = false;

const Login: React.FC<LoginProps> = ({
  setCurrentWindow,
  setAuthToken,
  onOtpDialogChange,
  validSSORequestId,
  consentForm,
}) => {
  const { t } = useLocale();
  const {
    state: { thirdPartyAuth },
    actions: { login },
  } = useAuthContext();
  const {
    state: { user },
    actions: { setUserLoginType, updateUser },
  } = useUserContext();
  const {
    state: { originUrl, shopifySessionId },
  } = useCheckoutContext();
  const {
    state: { merchant },
  } = useMerchantContext();
  const {
    state: { popup },
  } = useSSOContext();

  const { sendThirdPartyData } = useThirdPartyIntegration();

  const { sendAnalyticsEvent } = useSendAnalyticsEvent();
  const {
    actions: { getCustomerConsentsPayload },
  } = useUserConsentContext();
  const { ssoPermissions } = useJsonPermission();
  const isCtaBlockedByRule = Boolean(ssoPermissions.loginBtnStatus);

  const [searchParams] = useSearchParams();
  const [captchaToken, setCaptchaToken] = useState<CaptchaState>({
    token: null,
    status: CaptchaStatus.LOADING,
  });
  const isCaptchaValidating = merchant?.automationEnabled
    ? false
    : captchaToken.status === CaptchaStatus.LOADING || !Boolean(captchaToken.token);
  const isCheckoutFromCart = Boolean(searchParams.get("fromCart"));
  const shouldLoginToShopify = Boolean(searchParams.get("shouldLoginToShopify") || false);

  const [verifyOTPDialag, setVerifyOTPDialag] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [counter, setCounter] = useState<number>(-1);
  const [counterExpired, setCounterExpired] = useState<boolean>(false);
  const [phoneInputFocused, setPhoneInputFocused] = useState<boolean>(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState<Country | undefined>("IN");
  const [contextId, setContextId] = useState<string>("");
  const [isLoginStatusPolling, setIsLoginStatusPolling] = useState<boolean>(false);
  const [authRequestId, setAuthRequestId] = useState<string>("");
  const [showSuccessFullyLoggedIn, setShowSuccessFullyLoggedIn] = useState<boolean>(false);

  const {
    handleSubmit,
    inputProps,
    setValueOf,
    setErrors,
    handleFieldChange,
    state: { values, errors },
  } = useForm({
    initialState: verifyOTPDialag
      ? {
          otp: "",
        }
      : { phoneNumber: "" as PhoneInputValueType },
    validationSchema: verifyOTPDialag
      ? {
          otp: {
            required: t("enter_your_otp"),
            numeric: t("otp_incorrect"),
            length: {
              limit: 4,
              message: t("otp_incorrect"),
            },
            formatters: ["NUMERIC"],
          },
        }
      : {
          phoneNumber: {
            required: t("invalid_phone_number"),
            phone: t("invalid_phone_number"),
            formatters: ["PHONE"],
          },
        },
  });

  useEffect(() => {
    if (counter === 0) setCounterExpired(true);
    const timer: any = counter > 0 && setInterval(() => setCounter(counter - 1), 1000);
    return () => clearInterval(timer);
  }, [counter]);

  useEffect(() => {
    if (!Boolean(user?.phone)) return;
    setValueOf("phoneNumber", user?.phone);
  }, [user]);

  //Auto Read OTP for redirect
  const autoReadOTP = () => {
    if ("OTPCredential" in window) {
      const ac = new AbortController();
      const credentialOptions = {
        otp: { transport: ["sms"] },
        signal: ac.signal,
      };
      navigator.credentials
        .get(credentialOptions)
        .then((otp: any) => {
          setValueOf("otp", otp?.code);
          ac.abort();
        })
        .catch((err) => {
          ac.abort();
          console.error(err);
        });
    }
  };

  const isPostLoginConfigured = Boolean(popup?.postLogin);

  //Validate OTP handler
  const validateOtp = async (otp: string) => {
    if (Boolean(otp) && otp?.length !== 4) return;
    setErrors({ otp: { status: false } });
    setIsLoading(true);

    let startTime = new Date().getTime();
    let endTime;
    try {
      // OTP entered event
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_SSO_OTP_ENTERED,
        eventType: "load",
      });

      const result = await verifyOtp(contextId, otp, undefined, {
        bureau_event_id: searchParams.get("bureau_event_id") ?? "",
        customer_consents: getCustomerConsentsPayload("SSO"),
      });

      // Swap to success spinner only after verify succeeds (avoids layout jump on wrong OTP).
      setShowSuccessFullyLoggedIn(true);

      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_SSO_OTP_VERIFIED,
        eventType: "load",
      });

      endTime = new Date().getTime();
      const authToken = result?.access_token;
      const authTokenExpiry = result?.access_token_expires_at;
      const refreshToken = result?.refresh_token;
      const refreshTokenExpiry = result?.refresh_token_expires_at;

      setIsLoading(false);
      login(authToken, refreshToken, authTokenExpiry, refreshTokenExpiry, true);
      setAuthCookiesInLocalStorage(authToken, refreshToken, authTokenExpiry, refreshTokenExpiry);

      updateUser();

      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_SSO_LOGIN_COMPLETED,
        eventType: "load",
      });

      setAuthToken?.(authToken);
      publishPostMessage(eventTypes.FLO_SSO_TOKEN_RECEIVED, {
        accessToken: authToken,
        refreshToken: refreshToken,
        accessTokenExpiry: authTokenExpiry,
        refreshTokenExpiry: refreshTokenExpiry,
        phoneNumber: values?.phoneNumber,
        shouldLoginToShopify,
      });

      const requestId = searchParams.get("request-id");
      const userResponse = await getRequest(`/sso/user-details?request-id=${requestId}`, "AUTH_PRIVATE", {
        Authorization: authToken,
      });

      if (!Boolean(userResponse.first_name) || !Boolean(userResponse.email)) {
        setCurrentWindow(PopupWindow.USER_DETAILS);
        return;
      }

      if (merchant?.analytics?.webengage?.enabled) {
        sendThirdPartyData(
          {
            thirdParty: "webengage",
            type: (merchant?.analytics?.webengage?.userid_type as ThirdPartyData["type"]) ?? "phone",
          },
          {
            firstName: userResponse?.first_name,
            lastName: userResponse?.last_name,
            email: userResponse?.email,
            phone: userResponse?.phone_number,
            isLoaded: true,
          },
        );
      }

      if (merchant?.analytics?.moengage?.enabled) {
        sendThirdPartyData(
          {
            thirdParty: "moengage",
            type: (merchant?.analytics?.moengage?.userid_type as ThirdPartyData["type"]) ?? "phone",
          },
          {
            phone: userResponse?.phone_number,
            isLoaded: true,
          },
        );
      }

      if (merchant?.analytics?.clevertap?.enabled) {
        sendThirdPartyData({
          thirdParty: "clevertap",
          type: (merchant?.analytics?.clevertap?.userid_type as ThirdPartyData["type"]) ?? "phone",
        });
      }

      // we just set the current window the logic for this is handled in the marketing popup page
      if (isPostLoginConfigured) {
        setCurrentWindow(PopupWindow.POST_LOGIN);
      } else {
        const timeTaken = endTime - startTime;
        setTimeout(() => {
          publishPostMessage(eventTypes.FLO_MARKETING_POPUP_CLOSED, {
            shouldLoginToShopify,
          });
          setShowSuccessFullyLoggedIn(false);
        }, Math.min(2000, 5000 - timeTaken));
      }
    } catch (e: any) {
      setIsLoading(false);
      setShowSuccessFullyLoggedIn(false);
      setErrors({ otp: { status: true, message: t("otp_incorrect"), errorCode: getErrorCode(e) } });
    }
  };

  // Handles Phone number submission
  const handlePhoneNumberSubmit = async ({ phoneNumber }: { phoneNumber: string }) => {
    if (!(isPossiblePhoneNumber(phoneNumber) && isValidPhoneNumber(phoneNumber))) {
      errorToast(t("invalid_phone"));
      return;
    }
    try {
      setIsLoading(true);
      setErrors({ phoneNumber: { status: false }, otp: { status: false, showAlert: false } });
      setValueOf("otp", "");
      let verificationDetails: OTPVerificationDetails = {
        identifier: addDefaultCountryCode(phoneNumber),
        merchantId: merchant?.merchantId ?? "",
        domain: inIframe() ? originUrl : constants?.CHECKOUT_HOST,
      };

      let ssoRequestId: string | null | undefined;
      let contextMetada: OTPDeliveryType = {
        context: "AUTH",
      };
      ssoRequestId = validSSORequestId ?? searchParams.get("request-id");
      if (ssoRequestId) {
        contextMetada = {
          context: "SSO",
          requestId: ssoRequestId,
        };
      }

      const result = await sendOtpWithPhoneNumber(
        verificationDetails,
        contextMetada,
        captchaToken.token ?? "",
        merchant?.captchaProvider ?? "TURNSTILE",
      );
      setCounter(30);
      setCounterExpired(false);
      setIsLoading(false);
      if (!Boolean(result)) return;
      const isExistingUser: boolean = result.otp_required ?? result.existing_customer;
      setContextId(result.context_id);

      //Phone entered event
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_SSO_PHONE_ADDED,
        eventType: "load",
        metaData: {
          userType: isExistingUser ? "repeat" : "guest",
        },
      });

      if (!isExistingUser) {
        const authToken = result.token?.access_token;
        const expiry = result.token?.access_token_expires_at;
        login(authToken, "", expiry, undefined, isCheckoutFromCart);
        updateUser();
        setUserLoginType(analyticsEvents.FLO_GUEST_LOGIN as UserLoginType);
        sendAnalyticsEvent({
          eventName: analyticsEvents.FLO_GUEST_LOGIN,
          eventFor: [analyticsTypes.SF_ANALYTICS],
          eventType: "flo_action",
          metaData: {
            userData: {
              type: analyticsEvents.FLO_GUEST_LOGIN,
            },
            authSource: "SHOPFLO",
          },
        });
        return;
      }
      searchParams.set("phone", phoneNumber);
      setVerifyOTPDialag(true);
      onOtpDialogChange?.(true);

      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_SSO_OTP_LOADED,
        eventType: "load",
      });
    } catch (err: any) {
      setIsLoading(false);
      const errorCode = err?.response?.data?.error?.code;
      const errorMessage = err?.response?.data?.error?.message;
      if (errorCode === "GE_001") {
        errorToast(errorMessage || t("something_went_wrong"));
      } else if (err?.response?.status === 400) {
        setErrors({
          phoneNumber: {
            status: true,
            message: t("invalid_phone_number"),
          },
        });
      } else {
        errorToast(t("otp_fetch_failed"));
      }
    } finally {
      setCaptchaToken((prev) => ({ ...prev, token: null }));
    }
  };

  const handleResendOtp = async (channel: OTPChannel) => {
    setIsLoading(true);
    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_SSO_OTP_RESEND,
      eventType: "click",
    });
    setValueOf("otp", "");
    try {
      await resentOtp(contextId, channel);
      setCounter(30);
      setCounterExpired(false);
    } catch (err) {
      console.error(err);
    }
    setErrors({ otp: { status: false } });
    setIsLoading(false);
  };

  const getPrimaryButtonText = () => {
    const ssoCtaText = popup?.primaryCTA?.text ?? t("verify_otp");
    return ssoCtaText;
  };

  const pollThirdPartyAuthStatus = useCallback(async () => {
    if (!authRequestId) {
      stopAuthPolling();
      return;
    }
    let statusResponse;
    try {
      statusResponse = await getRequest(`/auth-callback/${authRequestId}/status`, "AUTH");
      if (statusResponse.status === "VERIFIED") {
        stopAuthPolling();
        const authToken = statusResponse?.metadata?.token_response?.access_token;
        const authTokenExpiry = statusResponse?.metadata?.token_response?.access_token_expires_at;
        const refreshToken = statusResponse?.metadata?.token_response?.refresh_token;
        const refreshTokenExpiry = statusResponse?.metadata?.token_response?.refresh_token_expires_at;
        const isExistingUser = statusResponse?.metadata?.token_response?.existing_user;
        login(authToken, refreshToken, authTokenExpiry, refreshTokenExpiry, isCheckoutFromCart);
        updateUser();
        setUserLoginType(
          isExistingUser
            ? (analyticsEvents.FLO_RETURN_USER_LOGIN as UserLoginType)
            : (analyticsEvents.FLO_GUEST_LOGIN as UserLoginType),
        );
        sendAnalyticsEvent({
          eventName: isExistingUser ? analyticsEvents.FLO_RETURN_USER_LOGIN : analyticsEvents.FLO_GUEST_LOGIN,
          eventType: "flo_action",
          metaData: {
            userData: {
              type: isExistingUser ? analyticsEvents.FLO_RETURN_USER_LOGIN : analyticsEvents.FLO_GUEST_LOGIN,
            },
            authSource: "TRUECALLER",
          },
        });

        return;
      }
      if (statusResponse.status === "FAILED" || statusResponse.status === "USER_REJECTED") {
        stopAuthPolling();
        return;
      }
      if (pollCounter >= 30) {
        stopAuthPolling();
        return;
      }
      pollCounter++;
    } catch (e) {
      console.error(e);
      stopAuthPolling();
    } finally {
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_TRUECALLER_ACTION,
        eventType: "flo_action",
        metaData: {
          action: {
            type: statusResponse?.status,
          },
        },
      });
    }
  }, [authRequestId]);

  const onPhoneInputFocus = () => {
    setPhoneInputFocused(true);
    if (Boolean(values?.phoneNumber?.length) || !thirdPartyAuth?.length || isPhoneInputFirstFocus) return;
    const truecallerRequest = thirdPartyAuth[0];
    if (!truecallerRequest) return;
    const authProvider = truecallerRequest.provider;

    switch (authProvider) {
      case "TRUECALLER": {
        setAuthRequestId(truecallerRequest.requestId);
        isPhoneInputFirstFocus = true;
        invokeTruecallerDeeplink(truecallerRequest.redirectUrl);
        setIsLoginStatusPolling(true);
        break;
      }
    }
  };

  const stopAuthPolling = () => {
    pollCounter = 1;
    setIsLoginStatusPolling(false);
  };

  const renderLoginHeader = useMemo(() => {
    if (!verifyOTPDialag) {
      return null;
    }
    // px-4 here + marketing-popup's px-4 wrapper = 32px, aligning with PopupHeader's ui-px-8.
    return (
      <div className="flex w-full flex-col gap-1 px-4 pb-6 pt-1">
        <h2 className="text-text-xl-semibold text-primary-dark">{t("otp_sent_title")}</h2>
        <p className="flex items-center gap-1.5 text-text-sm-medium text-coal-light opacity-70">
          <span>
            {t("otp_sent_to_label")} {setPhoneNumberSpacing(values.phoneNumber)}
          </span>
          <button
            type="button"
            onClick={() => {
              setVerifyOTPDialag(false);
              onOtpDialogChange?.(false);
            }}
            className="flex-shrink-0"
            aria-label={t("edit_phone_number") || "Edit phone number"}>
            <NotePencil className="h-4 w-4 text-coal-light" />
          </button>
        </p>
      </div>
    );
  }, [verifyOTPDialag, values.phoneNumber, t, onOtpDialogChange]);

  useEffect(() => {
    if (values.otp && values.otp.length === 4) {
      validateOtp(values.otp);
    }
  }, [values.otp]);

  useInterval(pollThirdPartyAuthStatus, isLoginStatusPolling ? 1500 : null);

  useEffect(() => {
    if (verifyOTPDialag) {
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_SSO_OTP_PAGE_LOADED,
        eventType: "load",
      });
    }
  }, [verifyOTPDialag]);

  return (
    <>
      {renderLoginHeader}
      {Boolean(verifyOTPDialag) ? (
        <>
          {/* OTP Dialog */}
          <div className={classNames("flex flex-col gap-4 rounded-xl p-0")}>
            {showSuccessFullyLoggedIn ? (
              <div className="flex h-full w-full items-center justify-center flex-col space-y-4">
                <Spinner className="spin-fast" />
                <ToggleLoadingText
                  texts={[t("logging you in"), t("fetching best offers"), t("almost there")]}
                  className="text-xs"
                />
              </div>
            ) : (
              <>
                <OTPForm
                  isLoading={isLoading}
                  otpLength={4}
                  otpValue={values.otp ?? ""}
                  handleSubmit={handleSubmit(validateOtp)}
                  handleChange={(code: string) => {
                    setValueOf("otp", code);
                    if (errors?.otp?.status) {
                      setErrors({ otp: { status: false } });
                    }
                  }}
                  validateOtp={validateOtp}
                  handleResendOtp={handleResendOtp}
                  error={errors?.otp?.status || false}
                  errorCode={errors?.otp?.errorCode}
                  errorMessage={t("otp_incorrect_login_sso")}
                  autoFocus={true}
                  otpCounter={counter}
                  otpCounterExpired={counterExpired}
                  emailValidation={false}
                  showLoginConsent={false}
                  context={"sso"}
                />
              </>
            )}
          </div>
        </>
      ) : (
        <>
          <form
            onSubmit={handleSubmit(handlePhoneNumberSubmit)}
            className="flex w-full flex-col items-center">
            <PhoneInput
              defaultCountry="IN"
              {...inputProps(FormField.phoneNumber, {
                onFocus: () => onPhoneInputFocus(),
                onBlur: () => setPhoneInputFocused(false),
                onChange: (value: PhoneInputValueType) => {
                  handleFieldChange(FormField.phoneNumber, value ?? "");
                },
              })}
              onCountryChange={(code) => setSelectedCountryCode(code)}
              numberInputProps={{
                maxLength: getPhoneInputMaxLength(values?.phoneNumber, selectedCountryCode),
              }}
              value={values?.phoneNumber ?? user?.phone ?? ("" as PhoneInputValueType)}
              countries={merchant?.addressConfig?.enabledCountries}
              id="flo__auth__phoneInput"
              smartCaret={false}
              className={classNames(
                "placeholder-sm peer w-full appearance-none shadow-[0_0_0_1px_rgba(0,0,0,0.08)] px-3 text-base bg-white rounded-xl h-12 !font-medium",
                errors.phoneNumber?.status ? "border border-ouch shadow-none" : "",
                Boolean(values?.phoneNumber?.length) && !errors?.phoneNumber?.status ? "shadow-none" : "",
                phoneInputFocused
                  ? `border-primary-dark border px-[12px] shadow-[0px_0px_4px_0px_#00000040] outline-none ring-[1px] ring-primary-dark`
                  : "",
              )}
              placeholder={t("phone_number")}
              international={true}
              countryCallingCodeEditable={false}
              autoFocus={true}
              limitMaxLength
              error={errors.phoneNumber?.message}
            />

            <p
              className={`w-full px-3 text-center font-normal ${
                errors.phoneNumber?.status ? "self-start text-sm text-ouch py-1" : "text-xs text-coal-light"
              }`}>
              {errors.phoneNumber?.status && errors.phoneNumber?.message}
            </p>
            <div className="w-full">
              <CustomComponentSlot position="AUTH_PHONE_SECTION" />
            </div>
            {consentForm?.isVisible && <ConsentCheckboxes consentForm={consentForm} className="px-2" />}
            <div className="w-full my-2">
              <PrimaryButton
                type="submit"
                buttonText={getPrimaryButtonText()}
                height="h-12"
                isLoading={isLoading || isCaptchaValidating}
                isDisabled={isCtaBlockedByRule}
                isCheckout={false}
                borderRadius="rounded-xl"
                id="flo__auth__loginButton"
                className={`${
                  captchaToken.status === CaptchaStatus.ERROR && isCaptchaValidating ? "!hidden" : "block"
                }`}
              />
            </div>

            <CaptchaWrapper
              context="SSO_LOGIN"
              onTokenChange={setCaptchaToken}
              status={captchaToken.status}
            />

            <div>
              <MarketingConsent variant="compact" />
            </div>
          </form>
        </>
      )}
    </>
  );
};

export default Login;
